/**
 * @Module: WelcomeOverlay
 * @Status: Ready
 * @Summary: 3-step onboarding — quick preferences, kitchen setup, start exploring
 * @Description: Step 1 collects dietary restrictions + household size (persists to
 *   PreferencesContext). Step 2 collects kitchen description (persists to
 *   KitchenContext). Step 3 shows navigation options. All steps are skippable.
 */

import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody,
  VStack, HStack, Text, Button, Textarea, Wrap, WrapItem, Badge,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper, Box,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { profileFromApi } from '../../types/kitchen';
import { useKitchen } from '../../contexts/KitchenContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { DIETARY_OPTIONS } from '../../types/preferences';
import { AppIcon } from '../icons/AppIcon';
import PantryScan from '../kitchen/PantryScan';

const STORAGE_KEY = 'choppy:hasSeenWelcome';

const WelcomeOverlay: React.FC = () => {
  const hasSeenWelcome = localStorage.getItem(STORAGE_KEY) === 'true';
  const [isOpen, setIsOpen] = useState(!hasSeenWelcome);
  const [step, setStep] = useState(0);
  const [kitchenText, setKitchenText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { updateProfile } = useKitchen();
  const { preferences, updatePreferences } = usePreferences();
  const navigate = useNavigate();
  const toast = useToast();

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
  };

  const navigateAndClose = (path: string) => {
    dismiss();
    navigate(path);
  };

  const toggleDietary = (value: string) => {
    const current = preferences.dietaryRestrictions;
    const updated = current.includes(value)
      ? current.filter(r => r !== value)
      : [...current, value];
    updatePreferences({ dietaryRestrictions: updated });
  };

  const handleSaveKitchen = async () => {
    if (!kitchenText.trim()) { setStep(2); return; }
    setIsSaving(true);
    try {
      const response = await api.kitchen.parse(kitchenText);
      const parsed = profileFromApi(response);
      updateProfile({ ...parsed, rawText: kitchenText });
      toast({
        title: 'Kitchen profile saved',
        status: 'success', duration: 2000, isClosable: true, position: 'top',
      });
      setStep(2);
    } catch {
      toast({
        title: 'Could not parse kitchen profile',
        description: 'No worries — you can set it up later.',
        status: 'warning', duration: 4000, isClosable: true, position: 'top',
      });
      setStep(2);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen onClose={dismiss} size="md" isCentered closeOnOverlayClick={false} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="surface.card" mx={4} borderRadius="xl" maxH="90vh">
        <ModalBody py={6} px={5} overflowY="auto">
          {/* Step 1: Quick preferences */}
          {step === 0 && (
            <VStack spacing={5} textAlign="center">
              <img src="/logo.svg" alt="Staple It" width={64} height={64} />
              <Text fontSize="xl" fontWeight="bold" fontFamily="heading" color="text.primary">
                Welcome to Staple It
              </Text>
              <Text fontSize="sm" color="text.secondary" lineHeight="1.6">
                Your kitchen companion. Let's personalize your experience.
              </Text>

              {/* Quick dietary picks */}
              <Box w="100%" textAlign="left">
                <Text fontSize="xs" fontWeight={600} color="text.muted" mb={2}
                  textTransform="uppercase" letterSpacing="wider">
                  Any dietary needs?
                </Text>
                <Wrap spacing={2}>
                  {DIETARY_OPTIONS.slice(0, 7).map(({ label, value }) => {
                    const active = preferences.dietaryRestrictions.includes(value);
                    return (
                      <WrapItem key={value}>
                        <Badge
                          as="button" px={3} py={1.5} borderRadius="full" fontSize="xs"
                          bg={active ? 'terracotta.400' : 'transparent'}
                          color={active ? 'white' : 'text.secondary'}
                          borderWidth="1px"
                          borderColor={active ? 'terracotta.400' : 'border.subtle'}
                          cursor="pointer"
                          transition="all 200ms"
                          _hover={{ bg: active ? 'terracotta.500' : 'surface.hover' }}
                          onClick={() => toggleDietary(value)}
                        >
                          {label}
                        </Badge>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              </Box>

              {/* Household size */}
              <Box w="100%" textAlign="left">
                <Text fontSize="xs" fontWeight={600} color="text.muted" mb={2}
                  textTransform="uppercase" letterSpacing="wider">
                  How many people?
                </Text>
                <NumberInput
                  value={preferences.householdSize}
                  onChange={(_, v) => updatePreferences({ householdSize: v || 2 })}
                  min={1} max={10} size="sm" maxW="120px"
                >
                  <NumberInputField bg="surface.input" borderColor="border.subtle" />
                  <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                </NumberInput>
              </Box>

              <Button variant="kitchen-primary" w="100%" borderRadius="lg"
                onClick={() => setStep(1)}>
                Next
              </Button>
              <Button variant="link" size="sm" color="text.muted" onClick={() => setStep(1)}>
                Skip
              </Button>
            </VStack>
          )}

          {/* Step 2: Kitchen Setup */}
          {step === 1 && (
            <VStack spacing={4} textAlign="center">
              <Text fontSize="lg" fontWeight="bold" fontFamily="heading" color="text.primary">
                Describe Your Kitchen
              </Text>
              <Text fontSize="sm" color="text.secondary">
                What equipment, pantry staples, and cooking tools do you have?
              </Text>
              <Textarea
                value={kitchenText}
                onChange={(e) => setKitchenText(e.target.value)}
                placeholder={"I have a stove, oven, and microwave. Basic pots and pans. I usually keep rice, pasta, and olive oil around. Intermediate cook, family of 3."}
                rows={4} bg="surface.input" borderRadius="lg" fontSize="sm"
                borderColor="border.subtle"
                _focus={{ borderColor: 'sage.400', boxShadow: 'focus' }}
              />
              <Button variant="kitchen-primary" w="100%" borderRadius="lg"
                onClick={handleSaveKitchen}
                isLoading={isSaving} loadingText="Parsing...">
                Save Kitchen
              </Button>
              <Button variant="link" size="sm" color="text.muted" onClick={() => setStep(2)}>
                Skip for now
              </Button>
            </VStack>
          )}

          {/* Step 3: Pantry Scan */}
          {step === 2 && (
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold" fontFamily="heading" color="text.primary">
                Stock Your Pantry
              </Text>
              <Text fontSize="sm" color="text.secondary" textAlign="center">
                Check what you already have against these starter recipes.
              </Text>
              <PantryScan
                onComplete={() => setStep(3)}
                onSkip={() => setStep(3)}
              />
            </VStack>
          )}

          {/* Step 4: Start Exploring */}
          {step === 3 && (
            <VStack spacing={4} textAlign="center">
              <AppIcon name="ui.checkCircle" size={40} color="var(--chakra-colors-sage-400)" />
              <Text fontSize="lg" fontWeight="bold" fontFamily="heading" color="text.primary">
                You're all set!
              </Text>
              <VStack spacing={3} w="100%">
                <Button variant="kitchen-primary" w="100%" borderRadius="lg"
                  onClick={() => navigateAndClose('/browse')}
                  leftIcon={<AppIcon name="ui.chef" size={18} />}>
                  Browse Recipes
                </Button>
                <Button variant="kitchen-secondary" w="100%" borderRadius="lg"
                  onClick={() => navigateAndClose('/generate')}
                  leftIcon={<AppIcon name="ui.wand" size={18} />}>
                  Generate a Recipe
                </Button>
                <Button variant="kitchen-outline" w="100%" borderRadius="lg"
                  onClick={() => navigateAndClose('/import')}>
                  Import Your Own
                </Button>
              </VStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WelcomeOverlay;
